/**
 * 「重點句」的即時套色。
 *
 * 重點句存的是「句子文字」錨點而不是改過的 HTML——考卷的 pages 是
 * 考試現場在用的內容，詳解功能絕不動它。顯示端拿錨點文字在渲染好的
 * DOM 裡找到該句、包上 <span> 套色；找不到（教務後來改了文章）就
 * 靜默跳過，頁面不會壞。
 *
 * 難點是句子常常「跨節點」：文章裡有 <b>、單字 span、題號徽章穿插，
 * 單一 text node 的比對（vocabHighlighter 那種）找不到整句。這裡的
 * 做法：把容器底下所有 text node 串成一條字串（記住每個字元屬於哪個
 * node 的哪個 offset），空白一律折疊成單一空格後比對，命中後回頭把
 * 相交的每一段 text node 各自切開包起來。
 *
 * 比對時可用 ignoreSelector 跳過某些元素（例如後台預覽的題號徽章
 * .rv-qmark——選取時已把它剔除，比對自然也要跳過，兩邊才對得起來）。
 */

/** 空白（含 &nbsp;）折疊成單一空格＋去頭尾——存檔與比對都用這個 */
export const normalizeText = (s) => String(s || '').replace(/\s+/g, ' ').trim();

const collectTextNodes = (root, ignoreSelector, skipClassName) => {
  const nodes = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
  let node = walker.nextNode();
  while (node) {
    const parent = node.parentElement;
    const skip =
      parent &&
      ((ignoreSelector && parent.closest(ignoreSelector)) ||
        (skipClassName && parent.closest(`.${skipClassName}`)) ||
        parent.closest('script,style'));
    if (!skip) {
      nodes.push(node);
    }
    node = walker.nextNode();
  }
  return nodes;
};

/**
 * 在 root 底下把 text（先 normalize）的每一次出現包成
 * <span class="{className}" {...attrs}>。回傳包了幾處。
 */
export const highlightTextIn = (root, text, { className, attrs = {}, ignoreSelector } = {}) => {
  const needle = normalizeText(text);
  if (!root || needle === '') {
    return 0;
  }

  // 已包過同 class 的內文不再進比對，重複套用不會疊床架屋
  //（className 可以帶多個 class，防重疊只認第一個）
  const nodes = collectTextNodes(root, ignoreSelector, String(className).split(' ')[0]);

  // raw：所有 text node 串起來；pos[i] = 第 i 個字元在哪個 node 的哪個 offset
  let raw = '';
  const pos = [];
  nodes.forEach((n, ni) => {
    const t = n.nodeValue || '';
    for (let i = 0; i < t.length; i++) {
      pos.push({ ni, off: i });
    }
    raw += t;
  });

  // normalized：空白折疊；normToRaw[j] = normalized 第 j 個字元的 raw index
  let norm = '';
  const normToRaw = [];
  let inSpace = true; // 開頭的空白直接吃掉（等同 trim 左邊）
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (/\s/.test(ch)) {
      if (!inSpace) {
        norm += ' ';
        normToRaw.push(i);
        inSpace = true;
      }
    } else {
      norm += ch;
      normToRaw.push(i);
      inSpace = false;
    }
  }

  // 找出全部命中（raw 範圍），由後往前包——前面的 offset 才不會失效
  const matches = [];
  let from = 0;
  for (;;) {
    const at = norm.indexOf(needle, from);
    if (at === -1) {
      break;
    }
    matches.push([normToRaw[at], normToRaw[at + needle.length - 1] + 1]);
    from = at + needle.length;
  }

  for (let m = matches.length - 1; m >= 0; m--) {
    const [rawStart, rawEnd] = matches[m];
    // 這個範圍相交到的 node（帶各自的 local 區間），同樣由後往前處理
    const pieces = [];
    for (let i = rawStart; i < rawEnd; ) {
      const { ni, off } = pos[i];
      const nodeLen = (nodes[ni].nodeValue || '').length;
      const localEnd = Math.min(nodeLen, off + (rawEnd - i));
      pieces.push({ ni, start: off, end: localEnd });
      i += localEnd - off;
    }
    for (let p = pieces.length - 1; p >= 0; p--) {
      const { ni, start, end } = pieces[p];
      const nodeEl = nodes[ni];
      const target = start > 0 ? nodeEl.splitText(start) : nodeEl;
      if (end - start < (target.nodeValue || '').length) {
        target.splitText(end - start);
      }
      const span = document.createElement('span');
      span.className = className;
      Object.keys(attrs).forEach((k) => span.setAttribute(k, attrs[k]));
      target.parentNode.insertBefore(span, target);
      span.appendChild(target);
    }
  }
  return matches.length;
};

/** 拆掉 root 底下所有 className 的包裹（內文放回原位） */
export const clearHighlightsIn = (root, className) => {
  if (!root) {
    return;
  }
  root.querySelectorAll(`.${className}`).forEach((span) => {
    const parent = span.parentNode;
    while (span.firstChild) {
      parent.insertBefore(span.firstChild, span);
    }
    parent.removeChild(span);
    parent.normalize();
  });
};

/**
 * 目前的文字選取（限定在 root 之內）。回傳 { text, rect } 或 null。
 * ignoreSelector 的元素（題號徽章等）會從選取文字裡剔除——
 * 存進錨點的是乾淨的句子，學生端的文章裡也沒有那些徽章。
 */
export const selectionTextWithin = (root, ignoreSelector) => {
  const sel = typeof window.getSelection === 'function' ? window.getSelection() : null;
  if (!root || !sel || sel.rangeCount === 0 || sel.isCollapsed) {
    return null;
  }
  const range = sel.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) {
    return null;
  }
  const holder = document.createElement('div');
  holder.appendChild(range.cloneContents());
  if (ignoreSelector) {
    holder.querySelectorAll(ignoreSelector).forEach((el) => el.remove());
  }
  const text = normalizeText(holder.textContent);
  if (text === '') {
    return null;
  }
  return { text, rect: range.getBoundingClientRect(), range };
};
